import React, { useState } from 'react';
import { trackFAQInteraction, trackButtonClick } from '../utils/analytics';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How does AkiMoon protect my privacy?",
      answer: "AkiMoon uses end-to-end encryption for all your health data. We follow PIPEDA compliance standards for Canadian privacy law, and you have full control over your data with options to export or delete it at any time. We never sell your personal information to third parties."
    },
    {
      question: "How accurate are the AI predictions?",
      answer: "Our AI models become more accurate over time as they learn your unique patterns. Initial predictions are based on general population data, but after 2-3 cycles, the accuracy improves significantly. We continuously refine our algorithms with expert medical validation."
    },
    {
      question: "What makes AkiMoon different from other period tracking apps?",
      answer: "AkiMoon combines AI-powered cycle tracking with personalized product recommendations, holistic wellness plans, and expert community access. We focus on the complete wellness journey, not just cycle tracking, with a strong emphasis on privacy and data security."
    },
    {
      question: "Can I use AkiMoon if I have irregular cycles?",
      answer: "Absolutely! AkiMoon is designed to handle irregular cycles. Our AI adapts to your unique patterns, whether regular or irregular, and provides personalized insights regardless of cycle length or predictability."
    },
    {
      question: "What products do you recommend?",
      answer: "We partner with carefully vetted brands that offer organic, hypoallergenic, and sustainable menstrual products. Our recommendations are based on your flow intensity, comfort preferences, skin sensitivity, and environmental impact considerations."
    },
    {
      question: "How do I access expert advice?",
      answer: "Premium members can access our expert Q&A platform where you can ask questions to nutritionists, gynecologists, and therapists. We also offer educational content and wellness tips curated by healthcare professionals."
    },
    {
      question: "Is there a free trial for Premium features?",
      answer: "Yes! We offer a 7-day free trial of Premium features so you can experience the full AkiMoon experience before committing. You can cancel anytime during the trial period."
    },
    {
      question: "What devices will AkiMoon be available on?",
      answer: "AkiMoon will be available on both iOS and Android devices. We're also developing a web version for desktop access. All your data syncs seamlessly across devices for a consistent experience."
    },
    {
      question: "How do I export my data?",
      answer: "Premium members can export their data in multiple formats (JSON, CSV) at any time. This includes cycle data, symptoms, wellness activities, and personal preferences. We believe you should always own your data."
    },
    {
      question: "What if I have technical issues?",
      answer: "We offer comprehensive customer support through in-app chat, email, and for Premium members, priority phone support. Our team is committed to helping you have the best possible experience with AkiMoon."
    }
  ];

  const toggleFAQ = (index) => {
    const action = openIndex === index ? 'Close' : 'Open';
    trackFAQInteraction(faqs[index].question, action);
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="section-padding bg-gray-50">
      <div className="container-max">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked
            <span className="gradient-text"> Questions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about AkiMoon. Can't find what you're looking for? 
            Contact us and we'll get back to you as soon as possible.
          </p>
        </div>

        {/* FAQ Grid */}
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200 rounded-xl"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Our team is here to help. Send us a message and we'll respond within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => {
                  document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                  trackButtonClick('Contact Us', 'FAQ Section');
                }}
                className="btn-primary"
              >
                Contact Us
              </button>
              <button 
                onClick={() => {
                  document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' });
                  trackButtonClick('Join Waitlist', 'FAQ Section');
                }}
                className="btn-secondary"
              >
                Join Waitlist
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ; 